import React, {
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  useEffect,
  useId,
} from "react";
import ReactDOM from "react-dom";
import { CaretPosition } from "../../../types/ui";
import { Caret } from "../types/ui";

interface TooltipWrapperProps {
  trigger: React.ReactNode;
  tooltip: React.ReactNode;
  caret?: Caret;
  width?: string;
  tooltipClassName?: string;
  wrapperClassName?: string;
  lazyRender?: boolean;
  ariaLabel?: string;
}

const VIEWPORT_PADDING = 8; // min distance from viewport edges
const MIN_TOOLTIP_HEIGHT = 200; // flip down if less than this space above trigger
const PORTAL_PADDING = 10; // padding on the portal div (must match padding: "10px" in style)

const TooltipWrapper: React.FC<TooltipWrapperProps> = ({
  trigger,
  tooltip,
  caret = { position: CaretPosition.BOTTOM, color: "#1C9C6D" },
  width = "200px",
  tooltipClassName = "",
  wrapperClassName = "",
  lazyRender = false,
  ariaLabel,
}) => {
  const popupId = useId();
  const pinned = useRef(false);
  const keyboardOpen = useRef(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasBeenHovered, setHasBeenHovered] = useState(false);
  const [isFlippedDown, setIsFlippedDown] = useState(false);
  const [caretStyle, setCaretStyle] = useState<React.CSSProperties>({});
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
  const tooltipRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEnter = useCallback(() => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    setHasBeenHovered(true);
    setIsHovered(true);
  }, []);

  const handleLeave = useCallback(() => {
    if (pinned.current) return;
    leaveTimer.current = setTimeout(() => setIsHovered(false), 100);
  }, []);

  const close = useCallback((restoreFocus = false) => {
    pinned.current = false;
    setIsHovered(false);
    if (restoreFocus) wrapperRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!ariaLabel || !isHovered) return;
    if (keyboardOpen.current) {
      keyboardOpen.current = false;
      tooltipRef.current?.focus();
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const active = document.activeElement;
      // A hover-only popover must not pull focus or swallow Escape meant for
      // whatever the user is actually in (e.g. a drawer's own handler).
      const ownsFocus =
        pinned.current ||
        wrapperRef.current?.contains(active) ||
        tooltipRef.current?.contains(active);
      if (!ownsFocus) {
        close();
        return;
      }
      event.stopPropagation();
      close(true);
    };
    const onOutside = (event: MouseEvent) => {
      if (
        !wrapperRef.current?.contains(event.target as Node) &&
        !tooltipRef.current?.contains(event.target as Node)
      )
        close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onOutside);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onOutside);
    };
  }, [ariaLabel, isHovered, close]);

  useEffect(() => {
    const dismiss = () => close(pinned.current);
    window.addEventListener("close-all-tooltips", dismiss);
    return () => {
      window.removeEventListener("close-all-tooltips", dismiss);
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    };
  }, [close]);

  const shouldRenderTooltip = lazyRender ? isHovered : true;

  const recalc = useCallback(() => {
    const el = tooltipRef.current;
    const wrapper = wrapperRef.current;
    if (!el || !wrapper) return;

    const wRect = wrapper.getBoundingClientRect();
    const tRect = el.getBoundingClientRect();
    // tRect includes PORTAL_PADDING on all sides. The visible card is (tRect.width - 2*PORTAL_PADDING).
    const cardW = tRect.width - PORTAL_PADDING * 2;
    const cardH = tRect.height - PORTAL_PADDING * 2;
    // Skip recalc if content hasn't rendered yet (lazyRender=true, first hover)
    if (cardW <= 0 || cardH <= 0) return;
    const cStyle: React.CSSProperties = {};

    // Use visualViewport for accurate dimensions on iOS Safari where the address
    // bar reduces visible area without changing window.innerHeight/innerWidth.
    const vvHeight = window.visualViewport?.height ?? window.innerHeight;
    const vvWidth = window.visualViewport?.width ?? window.innerWidth;
    const vvOffsetTop = window.visualViewport?.offsetTop ?? 0;

    let top: number;
    let left: number;

    // For BOTTOM: anchor by `bottom` so card grows upward when content loads.
    let bottomVal: number | undefined;

    if (caret.position === CaretPosition.BOTTOM) {
      bottomVal = vvHeight + vvOffsetTop - wRect.top;
      top = 0; // unused for positioning, only for flip detection below
      left = wRect.left + wRect.width / 2 - cardW / 2 - PORTAL_PADDING;
    } else if (caret.position === CaretPosition.TOP) {
      top = wRect.bottom - PORTAL_PADDING;
      left = wRect.left + wRect.width / 2 - cardW / 2 - PORTAL_PADDING;
    } else if (caret.position === CaretPosition.LEFT) {
      left = wRect.left - cardW - PORTAL_PADDING;
      top = wRect.top + wRect.height / 2 - cardH / 2 - PORTAL_PADDING;
    } else {
      left = wRect.right - PORTAL_PADDING;
      top = wRect.top + wRect.height / 2 - cardH / 2 - PORTAL_PADDING;
    }

    const isVertical =
      caret.position === CaretPosition.BOTTOM ||
      caret.position === CaretPosition.TOP;

    // Horizontal viewport clamping (vertical tooltips only)
    if (isVertical) {
      const cardRight = left + PORTAL_PADDING + cardW;
      const cardLeft = left + PORTAL_PADDING;
      // Clamp the padded portal, not just the visible card. Otherwise its
      // invisible 10px padding protrudes beyond the viewport by 2px.
      if (cardRight > vvWidth - VIEWPORT_PADDING - PORTAL_PADDING) {
        const clampedCardLeft =
          vvWidth - VIEWPORT_PADDING - PORTAL_PADDING - cardW;
        const triggerCenterX = wRect.left + wRect.width / 2;
        const caretLeftPx = triggerCenterX - clampedCardLeft;
        cStyle.left = `${Math.max(12, Math.min(caretLeftPx, cardW - 12))}px`;
        cStyle.transform = "translateX(-50%)";
        left = clampedCardLeft - PORTAL_PADDING;
      } else if (cardLeft < VIEWPORT_PADDING + PORTAL_PADDING) {
        const clampedCardLeft = VIEWPORT_PADDING + PORTAL_PADDING;
        const triggerCenterX = wRect.left + wRect.width / 2;
        const caretLeftPx = triggerCenterX - clampedCardLeft;
        cStyle.left = `${Math.max(12, Math.min(caretLeftPx, cardW - 12))}px`;
        cStyle.transform = "translateX(-50%)";
        left = clampedCardLeft - PORTAL_PADDING;
      }
    }

    // Vertical flip: open on whichever side the card actually fits, falling
    // back to the MIN_TOOLTIP_HEIGHT floor so a sliver of space above never
    // wins. Both sides subtract VIEWPORT_PADDING and vvOffsetTop so the
    // decision uses the same room the maxHeight below will grant.
    //
    // Natural height comes from scrollHeight, not the measured rect: the rect
    // is already clamped by the maxHeight set on the previous pass, so a
    // tooltip whose content arrives asynchronously (a fetch inside the card)
    // would compare its clamped height against the space that clamped it and
    // never flip.
    let flippedDown = false;
    if (caret.position === CaretPosition.BOTTOM && bottomVal !== undefined) {
      const naturalHeight = el.scrollHeight;
      const spaceAbove = wRect.top - vvOffsetTop - VIEWPORT_PADDING;
      const spaceBelow =
        vvHeight + vvOffsetTop - wRect.bottom - VIEWPORT_PADDING;
      const doesFitAbove = naturalHeight <= spaceAbove;
      if (
        spaceAbove < MIN_TOOLTIP_HEIGHT ||
        (!doesFitAbove && spaceBelow > spaceAbove)
      ) {
        bottomVal = undefined;
        // Leave the same PORTAL_PADDING gap the upward layout leaves, so the
        // caret has somewhere to sit instead of overlapping the trigger row.
        top = wRect.bottom;
        flippedDown = true;
      }
    }

    const newPortalStyle: React.CSSProperties = { position: "fixed", left };
    if (bottomVal !== undefined) {
      newPortalStyle.bottom = bottomVal;
      newPortalStyle.top = "auto"; // explicitly clear base top:0 so bottom anchoring works
      newPortalStyle.maxHeight = wRect.top - vvOffsetTop - VIEWPORT_PADDING;
    } else {
      newPortalStyle.top = top;
      newPortalStyle.maxHeight =
        vvHeight + vvOffsetTop - top - VIEWPORT_PADDING;
    }
    newPortalStyle.overflowY = "auto";
    setPortalStyle(newPortalStyle);
    setCaretStyle(cStyle);
    setIsFlippedDown(flippedDown);
  }, [caret.position]);

  useLayoutEffect(() => {
    if (isHovered) {
      recalc();
    } else {
      setIsFlippedDown(false);
      setCaretStyle({});
      setPortalStyle({});
    }
  }, [isHovered, recalc]);

  // Re-run recalc when tooltip size changes (e.g. async content loads inside BiteOverview).
  // Guard against infinite loop: only recalc when dimensions actually change.
  useEffect(() => {
    const el = tooltipRef.current;
    if (!el || !isHovered) return;
    let lastW = 0;
    let lastH = 0;
    let rafId: number | null = null;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width !== lastW || height !== lastH) {
        lastW = width;
        lastH = height;
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          rafId = null;
          recalc();
        });
      }
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isHovered, recalc]);

  // Close tooltip on scroll (portal is fixed, so position becomes stale when page scrolls).
  // Ignore scroll events that originate inside the tooltip itself (e.g. BiteOverview overflow-y-auto).
  useEffect(() => {
    if (!isHovered) return;
    const onScroll = (e: Event) => {
      const path = e.composedPath?.() ?? [];
      if (tooltipRef.current && path.includes(tooltipRef.current)) return;
      close();
    };
    window.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [isHovered, close]);

  const caretBase = "absolute w-0 h-0";
  // Both variants offset with a position property, never a vertical translate:
  // the horizontal clamp above sets an inline `transform: translateX(-50%)`,
  // which would replace the class transform wholesale and drop the vertical
  // shift, dropping the caret onto the card.
  const caretClass = isFlippedDown
    ? `${caretBase} left-1/2 -translate-x-1/2 -top-[9px] border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent`
    : `${caretBase} left-1/2 -translate-x-1/2 -bottom-[9px] border-l-[8px] border-r-[8px] border-t-[8px] border-l-transparent border-r-transparent`;
  const caretBorderStyle = isFlippedDown
    ? { borderBottomColor: caret.color }
    : { borderTopColor: caret.color };

  const tooltipPortal = ReactDOM.createPortal(
    <div
      ref={tooltipRef}
      id={popupId}
      tabIndex={ariaLabel ? -1 : undefined}
      role={ariaLabel ? "dialog" : undefined}
      aria-label={ariaLabel ? `${ariaLabel} details` : undefined}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      style={{
        position: "fixed",
        top: 0,
        left: -9999,
        ...portalStyle,
        padding: `${PORTAL_PADDING}px`,
        visibility: isHovered ? "visible" : "hidden",
        zIndex: 9999,
        pointerEvents: isHovered ? "auto" : "none",
      }}
    >
      <div
        className={`${ariaLabel ? "et-analytics-detail-popover" : ""} relative overflow-visible p-4 bg-white text-[#252525] shadow-[0_0_10px_0_rgba(0,0,0,0.1)] text-sm rounded-[10px] ${tooltipClassName}`}
        style={{ width }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            close(!!ariaLabel);
          }}
          className={`${ariaLabel ? "flex" : "hidden [@media(hover:none)]:flex"} absolute top-2 right-2 w-5 h-5 items-center justify-center et-analytics-muted text-[#25252566] rounded z-10`}
          aria-label="Close"
        >
          ×
        </button>
        {shouldRenderTooltip && tooltip}
        {caret && (
          <div
            className={caretClass}
            style={{ ...caretBorderStyle, ...caretStyle }}
          />
        )}
      </div>
    </div>,
    document.body,
  );

  return (
    <>
      <div
        ref={wrapperRef}
        role={ariaLabel ? "button" : undefined}
        tabIndex={ariaLabel ? 0 : undefined}
        aria-label={ariaLabel}
        aria-haspopup={ariaLabel ? "dialog" : undefined}
        aria-expanded={ariaLabel ? isHovered : undefined}
        aria-controls={ariaLabel && isHovered ? popupId : undefined}
        className={`relative inline-block ${wrapperClassName}`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={() => {
          if (ariaLabel) pinned.current = true;
          handleEnter();
        }}
        onKeyDown={
          ariaLabel
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  pinned.current = true;
                  keyboardOpen.current = true;
                  handleEnter();
                }
              }
            : undefined
        }
      >
        <div className={wrapperClassName ? "w-full h-full" : ""} title="">
          {trigger}
        </div>
      </div>
      {hasBeenHovered && tooltipPortal}
    </>
  );
};

export default TooltipWrapper;
